import type { EventHandlerResponse } from 'h3'
import { jwtDecode } from 'jwt-decode'
import type { FetchContext } from 'ofetch'
import { UTM_COOKIES } from '~/utils/constants'

export const getUtmCookies = () => {
  try {
    let cookies = ''

    UTM_COOKIES.map((name) => {
      const fullName = 'bc_ft_' + name
      const cookie = useCookie(fullName, { maxAge: 60 * 60 * 24 * 120 })
      if (cookie.value) {
        cookies = cookies + `${fullName}=${cookie.value}; `
      }
    })

    return cookies
  } catch {
    return ''
  }
}

export const getHeaders = () => {
  const { token } = useAuth()

  const headers: Record<string, string> = {
    'X-Device-Type': 'Website'
  }
  const utmCookies = getUtmCookies()

  if (token.value) {
    headers['X-Authorization'] = token.value
  }

  if (utmCookies) {
    headers['X-Utm-Cookie'] = utmCookies
  }

  return headers
}

interface onResponseErrorParams {
  response: EventHandlerResponse
}

export async function onResponseError({ response }: onResponseErrorParams) {
  if (response?.status === 401) {
    const { signOut } = useAuth()
    await signOut({ redirect: false })

    const route = useRoute()
    if (route.meta?.middleware === 'authentication') {
      await navigateTo('/')
    }
  }
}

export async function checkAndRefreshToken({ options }: FetchContext) {
  const { refreshToken, refresh, token } = useAuth()

  if (!refreshToken.value) return

  let shouldRefresh = true

  if (token.value) {
    const decodedToken = jwtDecode(token.value)

    shouldRefresh = !!(
      decodedToken.exp && decodedToken.exp <= Math.floor(Date.now() / 1000)
    )
  }

  if (shouldRefresh) {
    try {
      await refresh()
      options.headers.set('X-Authorization', token.value as string)
    } catch (error) {
      const { rawRefreshToken } = useAuthState()
      rawRefreshToken.value = null
      const { $sentryCaptureException } = useNuxtApp()
      $sentryCaptureException(error)
    }
  }
}

export async function useFetchApi(url: string, options = { headers: {} }) {
  const config = useRuntimeConfig()

  return useFetch(url, {
    ...options,
    baseURL: config.public.API_URL,
    headers: { ...getHeaders(), ...options.headers },
    onRequest: checkAndRefreshToken,
    onResponseError
  })
}

export async function $fetchApi(url: string, options = { headers: {} }) {
  const config = useRuntimeConfig()

  return $fetch(url, {
    ...options,
    baseURL: config.public.API_URL,
    headers: { ...getHeaders(), ...options.headers },
    onRequest: checkAndRefreshToken,
    onResponseError
  })
}
